import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Eye, LogOut, Search, User } from 'lucide-react';
import type { UserView } from '@geoic/shared';
import { ROLE_LABELS } from '@/components/ui/Badge.js';

export type TopbarProps = {
  user: UserView;
  onLogout: () => void;
  /** Saytning ommaviy manzili — «Saytni ko'rish» tugmasi uchun. */
  siteUrl?: string;
};

/**
 * Yuqori panel — MAKETDAN (`D1`, BOSHQARUV PANELI).
 *
 * Chapda qidiruv maydoni (ariza raqami, mahsulot yoki mijoz), o'ngda
 * «Saytni ko'rish», keyin foydalanuvchi bloki. Balandligi 62px — yon
 * menyu sarlavhasi bilan bir chiziqda tursin.
 *
 * Qidiruv arizalar ro'yxatiga yuboradi: adminda global qidiruv yo'q,
 * lekin maketdagi maydonning ma'nosi «ariza raqami yoki mijoz» —
 * ya'ni aynan o'sha ekran.
 */
export function Topbar({ user, onLogout, siteUrl }: TopbarProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;

    // Tashqariga bosilganda yopiladi — menyu ochiq qolib, sahifani
    // to'sib turmasin.
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <header className="flex h-[62px] shrink-0 items-center gap-5 border-b border-n-200 bg-white px-4 md:px-6">
      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = query.trim();
          navigate(trimmed ? `/requests?search=${encodeURIComponent(trimmed)}` : '/requests');
        }}
        className="flex max-w-[420px] flex-1 items-center gap-2.5 border border-n-200 px-3.5 focus-within:border-brand-600"
      >
        <Search className="size-[17px] shrink-0 text-n-600" aria-hidden />
        <label className="sr-only" htmlFor="admin-search">
          Qidirish
        </label>
        <input
          id="admin-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Ariza raqami, mahsulot yoki mijoz"
          className="h-10 min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
        />
      </form>

      <div className="ml-auto flex items-center gap-2.5">
        {siteUrl && (
          <a
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden h-[38px] items-center gap-2.5 border border-n-200 px-4 text-[13.5px] font-medium text-n-800 hover:border-brand-600 sm:flex"
          >
            <Eye className="size-4" aria-hidden />
            Saytni ko‘rish
          </a>
        )}

        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-haspopup="menu"
            className="flex items-center gap-2.5 border-l border-n-200 py-1.5 pl-3.5 text-sm"
          >
            {/* Maketda avatar KVADRAT — dizayn tizimida burchak to‘g‘ri */}
            <span className="flex size-[34px] items-center justify-center bg-brand-600 text-[12.5px] font-bold text-white">
              {initials(user.fullName)}
            </span>
            <span className="hidden text-left sm:block">
              <span className="block text-[13px] leading-tight font-semibold text-n-900">
                {user.fullName}
              </span>
              <span className="block text-[11.5px] leading-tight text-n-600">
                {ROLE_LABELS[user.role]}
              </span>
            </span>
            <ChevronDown className="size-[15px] text-n-600" aria-hidden />
          </button>

          {open && (
            <div
              role="menu"
              className="absolute right-0 z-20 mt-1 w-56 border border-n-200 bg-white py-1"
            >
              <div className="border-b border-n-100 px-3 py-2">
                <p className="truncate text-sm font-medium text-n-900">{user.fullName}</p>
                <p className="truncate text-xs text-n-600">{user.email}</p>
              </div>

              {/*
                `<a>` emas, `<Link>`: oddiy havola butun sahifani qayta
                yuklardi va seans tekshiruvi boshidan takrorlanardi.
              */}
              <Link
                to="/profile"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-n-800 hover:bg-n-50"
              >
                <User className="size-4" aria-hidden />
                Profil va parol
              </Link>

              <button
                type="button"
                role="menuitem"
                onClick={onLogout}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error-600 hover:bg-error-50"
              >
                <LogOut className="size-4" aria-hidden />
                Chiqish
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/** «Alisher Rahmonov» → «AR». Bitta so'z bo'lsa bitta harf. */
export function initials(fullName: string): string {
  return fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}
